import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { batchAnalyze } from '../api/scout';
import { Spinner } from '../components/LoadingState';
import { useTheme, ACCENT } from '../contexts/ThemeContext';

function RecentAnalyses() {
  const [recent, setRecent] = useState([]);
  const { t } = useTheme();

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('scout_recent') || '[]');
      setRecent(stored);
    } catch { /* ignore */ }
  }, []);

  if (!recent.length) return null;

  return (
    <div style={{ marginTop: '36px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <span style={{
          fontSize: '10px', fontWeight: 700, color: t.textDim,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Recent Analyses
        </span>
        <button
          onClick={() => { localStorage.removeItem('scout_recent'); setRecent([]); }}
          style={{
            fontSize: '9px', color: t.textFaint, background: 'none', border: 'none',
            cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}
        >
          Clear
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {recent.slice(0, 10).map((item, i) => (
          <Link
            key={i}
            to={`/ticket/${item.ticketId}`}
            className="card-hover"
            style={{ padding: '10px 14px', textDecoration: 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <span className="font-mono" style={{ color: ACCENT, fontSize: '12px', fontWeight: 600 }}>
                  #{item.ticketId}
                </span>
                <span style={{
                  fontSize: '13px', color: t.textMuted, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {item.summary}
                </span>
              </div>
              <span className="font-mono" style={{
                fontSize: '10px', color: t.textFaint, whiteSpace: 'nowrap',
                fontWeight: 500, marginLeft: '12px',
              }}>
                {item.timestamp}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTheme();
  const [investigateText, setInvestigateText] = useState('');
  const [ticketInput, setTicketInput] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleInvestigate(e) {
    e.preventDefault();
    if (!investigateText.trim()) return;
    navigate('/investigate', { state: { text: investigateText.trim() } });
  }

  async function handleTicketLookup(e) {
    e.preventDefault();
    const ids = ticketInput.split(/[,\s]+/).map(s => s.replace('#', '').trim()).filter(Boolean);
    if (!ids.length) return;
    if (ids.length === 1) { navigate(`/ticket/${ids[0]}`); return; }
    setTicketLoading(true);
    setError('');
    try {
      const result = await batchAnalyze(ids);
      navigate('/csat', { state: { batchResults: result } });
    } catch (err) {
      setError(err.message);
    } finally {
      setTicketLoading(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div style={{ marginBottom: '36px' }}>
        <h1 style={{
          fontSize: '22px', fontWeight: 900, color: t.text,
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          What would you like to investigate?
        </h1>
        <p style={{
          color: t.textDim, marginTop: '8px', fontSize: '12px',
          fontWeight: 600, letterSpacing: '0.04em',
        }}>
          Search tickets, review CSAT ratings, or investigate customer complaints
        </p>
      </div>

      {error && (
        <div style={{
          marginBottom: '24px', padding: '12px 16px', borderRadius: '10px',
          background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)',
          borderLeft: '2px solid #F07070',
          color: '#F07070', fontSize: '12px', fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Two action cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Investigate Card */}
        <form onSubmit={handleInvestigate} className="card flex flex-col">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <div style={{
              width: '3px', height: '20px', background: ACCENT,
            }} />
            <div>
              <h3 style={{
                fontSize: '13px', fontWeight: 900, color: t.text,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                Investigate
              </h3>
              <p style={{
                fontSize: '10px', color: t.textDim, fontWeight: 600,
                letterSpacing: '0.04em', marginTop: '2px',
              }}>
                Find matching Zendesk tickets
              </p>
            </div>
          </div>
          <textarea
            value={investigateText}
            onChange={e => setInvestigateText(e.target.value)}
            placeholder="Paste a review, Reddit post, or customer email..."
            rows={5}
            className="textarea flex-1 mb-4 w-full"
          />
          <button type="submit" disabled={!investigateText.trim()} className="btn-primary w-full">
            SEARCH ZENDESK
          </button>
        </form>

        {/* CSAT Review Card */}
        <div
          onClick={() => navigate('/csat')}
          className="card-hover flex flex-col"
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <div style={{
              width: '3px', height: '20px', background: ACCENT,
            }} />
            <div>
              <h3 style={{
                fontSize: '13px', fontWeight: 900, color: t.text,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                CSAT Review
              </h3>
              <p style={{
                fontSize: '10px', color: t.textDim, fontWeight: 600,
                letterSpacing: '0.04em', marginTop: '2px',
              }}>
                Pull and categorize negative ratings
              </p>
            </div>
          </div>
          <div className="flex-1 flex items-end">
            <div style={{
              width: '100%', padding: '20px', borderRadius: '10px', textAlign: 'center',
              background: 'rgba(255,255,255,0.02)', border: `1px solid ${t.border}`,
            }}>
              <p style={{
                color: t.textDim, fontSize: '11px', fontWeight: 600,
                letterSpacing: '0.04em', marginBottom: '12px',
              }}>
                Select dates, pull ratings, analyze patterns
              </p>
              <span style={{
                color: ACCENT, fontSize: '11px', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                OPEN CSAT REVIEW →
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Ticket Lookup */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          <div style={{
            width: '3px', height: '20px', background: ACCENT,
          }} />
          <div>
            <h3 style={{
              fontSize: '13px', fontWeight: 900, color: t.text,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Quick Ticket Lookup
            </h3>
            <p style={{
              fontSize: '10px', color: t.textDim, fontWeight: 600,
              letterSpacing: '0.04em', marginTop: '2px',
            }}>
              Enter ticket numbers for instant analysis
            </p>
          </div>
        </div>
        <form onSubmit={handleTicketLookup} className="flex gap-3">
          <input
            type="text"
            value={ticketInput}
            onChange={e => setTicketInput(e.target.value)}
            placeholder="e.g. 123456, 789012"
            className="input flex-1"
          />
          <button type="submit" disabled={!ticketInput.trim() || ticketLoading} className="btn-primary flex items-center gap-2">
            {ticketLoading ? <Spinner size="sm" /> : null}
            ANALYZE
          </button>
        </form>
      </div>

      <RecentAnalyses />
    </div>
  );
}
